const toNumericId = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (/^-?\d+$/.test(trimmedValue)) {
      const numericValue = Number(trimmedValue);
      return Number.isFinite(numericValue) ? numericValue : null;
    }
  }

  return null;
};

const getMarkerCandidateId = (marker) => {
  if (marker && typeof marker === "object") {
    return marker.id ?? marker.marker_id ?? marker.markerId ?? null;
  }

  return marker;
};

const normalizeMarkerInput = (markers) => {
  if (typeof markers === "string") {
    return markers.split(",");
  }

  if (Array.isArray(markers)) {
    return markers.flat(Infinity);
  }

  return [];
};

export const normalizeOverviewMarkerIds = (markers = []) => {
  const seen = new Set();
  const normalizedMarkers = [];

  normalizeMarkerInput(markers).forEach((marker) => {
    const candidateId = getMarkerCandidateId(marker);
    if (candidateId === null || candidateId === undefined) {
      return;
    }

    const numericCandidate = toNumericId(candidateId);
    const normalizedCandidate =
      numericCandidate !== null ? numericCandidate : String(candidateId).trim();

    if (
      normalizedCandidate === null ||
      normalizedCandidate === undefined ||
      normalizedCandidate === ""
    ) {
      return;
    }

    const markerKey = String(normalizedCandidate);
    if (seen.has(markerKey)) {
      return;
    }

    seen.add(markerKey);
    normalizedMarkers.push(normalizedCandidate);
  });

  return normalizedMarkers;
};

export const getOverviewVideoMarkerIds = (parameter) =>
  normalizeOverviewMarkerIds(parameter?.markers);

export const getOverviewVideoCount = (parameter) => {
  const uniqueCount = Number(
    parameter?.unique_markers_count ?? parameter?.event_count ?? parameter?.unique_count
  );
  if (Number.isFinite(uniqueCount) && uniqueCount > 0) {
    return uniqueCount;
  }
  return getOverviewVideoMarkerIds(parameter).length;
};

const isPercentageParameter = (parameter) =>
  String(parameter?.parameter_name ?? parameter?.name ?? "").includes("%");

// A percentage's markers are its numerator's events, so their count would show
// e.g. Shots on target in place of Shots on target, %. Show the percentage itself.
export const getOverviewStatDisplayCount = (parameter) => {
  if (!isPercentageParameter(parameter)) {
    return getOverviewVideoCount(parameter);
  }

  const percentage = Number(parameter?.count ?? parameter?.value ?? 0);
  return Number.isFinite(percentage) ? percentage : 0;
};

export const mergeOverviewVideoParameters = (...parameters) => {
  const validParameters = parameters.filter(Boolean);

  if (validParameters.length === 0) {
    return null;
  }

  const [primaryParameter] = validParameters;
  const markerIds = normalizeOverviewMarkerIds(
    validParameters.flatMap((parameter) =>
      normalizeOverviewMarkerIds(parameter?.markers)
    )
  );
  const rawCount = validParameters.reduce((sum, parameter) => {
    const nextCount = Number(parameter?.count ?? parameter?.value ?? 0);
    return sum + (Number.isFinite(nextCount) ? nextCount : 0);
  }, 0);
  const uniqueCount = validParameters.reduce((sum, parameter) => {
    const nextCount = Number(
      parameter?.event_count ?? parameter?.unique_markers_count ?? parameter?.unique_count
    );
    return sum + (Number.isFinite(nextCount) ? nextCount : 0);
  }, 0);
  const hasUniqueCount = validParameters.some((parameter) =>
    Number.isFinite(
      Number(parameter?.event_count ?? parameter?.unique_markers_count ?? parameter?.unique_count)
    )
  );

  return {
    ...primaryParameter,
    count: rawCount,
    value: rawCount,
    event_count: hasUniqueCount ? uniqueCount : markerIds.length,
    unique_count: hasUniqueCount ? uniqueCount : markerIds.length,
    unique_markers_count: hasUniqueCount ? uniqueCount : markerIds.length,
    markers: markerIds,
  };
};
